import logger from '@/utils/logger';
/**
 * 操作结果
 */
export interface OperationResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  fromCache: boolean;
  executionTime: number;
}

/**
 * 正在执行的操作
 */
interface PendingOperation<T> {
  promise: Promise<T>;
  startTime: number;
  operationId: string;
}

/**
 * 操作防护器 - 防止重复执行相同操作
 */
export class OperationGuard {
  private pendingOperations: Map<string, PendingOperation<any>> = new Map();

  private lastExecutionTimes: Map<string, number> = new Map();

  private maxCacheSize = 100; // 限制缓存大小

  /**
   * 执行操作，如果操作正在进行中则返回现有的Promise
   */
  async execute<T>(
    operationId: string,
    operation: () => Promise<T>,
    debounceMs = 0
  ): Promise<OperationResult<T>> {
    const now = Date.now();

    // 检查防抖时间
    if (debounceMs > 0) {
      const lastExecution = this.lastExecutionTimes.get(operationId);
      if (lastExecution && now - lastExecution < debounceMs) {
        logger.info(
          `[OperationGuard] 操作被防抖跳过: ${operationId} (${
            now - lastExecution
          }ms < ${debounceMs}ms)`
        );
        return {
          success: false,
          error: new Error(`操作过于频繁，请等待 ${debounceMs - (now - lastExecution)}ms`),
          fromCache: false,
          executionTime: 0,
        };
      }
    }

    // 检查是否有正在执行的相同操作
    const pending = this.pendingOperations.get(operationId);
    if (pending) {
      logger.info(`[OperationGuard] 使用现有操作: ${operationId}`);
      try {
        const data = await pending.promise;
        return {
          success: true,
          data,
          fromCache: true,
          executionTime: now - pending.startTime,
        };
      } catch (error) {
        return {
          success: false,
          error: error as Error,
          fromCache: true,
          executionTime: now - pending.startTime,
        };
      }
    }

    // 执行新操作
    logger.info(`[OperationGuard] 开始新操作: ${operationId}`);

    const operationPromise = this.executeWithCleanup(operationId, operation);

    // 注册正在执行的操作
    this.pendingOperations.set(operationId, {
      promise: operationPromise,
      startTime: now,
      operationId,
    });

    try {
      const data = await operationPromise;
      const executionTime = Date.now() - now;

      // 记录执行时间
      this.lastExecutionTimes.set(operationId, now);
      this.limitCacheSize();

      logger.info(`[OperationGuard] 操作完成: ${operationId} (耗时: ${executionTime}ms)`);

      return {
        success: true,
        data,
        fromCache: false,
        executionTime,
      };
    } catch (error) {
      const executionTime = Date.now() - now;
      logger.error(`[OperationGuard] 操作失败: ${operationId} (耗时: ${executionTime}ms)`, error);

      return {
        success: false,
        error: error as Error,
        fromCache: false,
        executionTime,
      };
    }
  }

  /**
   * 执行操作并确保清理
   */
  private async executeWithCleanup<T>(
    operationId: string,
    operation: () => Promise<T>
  ): Promise<T> {
    try {
      return await operation();
    } finally {
      // 无论成功失败都要清理正在执行的操作
      this.pendingOperations.delete(operationId);
    }
  }

  /**
   * 检查操作是否正在执行
   */
  isExecuting(operationId: string): boolean {
    return this.pendingOperations.has(operationId);
  }

  /**
   * 取消操作（如果支持的话）
   */
  cancel(operationId: string): boolean {
    const pending = this.pendingOperations.get(operationId);
    if (pending) {
      this.pendingOperations.delete(operationId);
      logger.info(`[OperationGuard] 操作已取消: ${operationId}`);
      return true;
    }
    return false;
  }

  /**
   * 取消所有操作
   */
  cancelAll(): string[] {
    const cancelledOperations = Array.from(this.pendingOperations.keys());
    this.pendingOperations.clear();

    logger.info(`[OperationGuard] 已取消所有操作 (${cancelledOperations.length}个)`);
    return cancelledOperations;
  }

  /**
   * 等待指定操作完成
   */
  async waitFor<T>(operationId: string, timeoutMs = 10000): Promise<T | null> {
    const pending = this.pendingOperations.get(operationId);
    if (!pending) {
      return null;
    }

    try {
      // 添加超时控制
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error(`等待操作超时: ${operationId}`)), timeoutMs);
      });

      return await Promise.race([pending.promise, timeoutPromise]);
    } catch (error) {
      logger.error(`[OperationGuard] 等待操作失败: ${operationId}`, error);
      return null;
    }
  }

  /**
   * 获取正在执行的操作列表
   */
  getPendingOperations(): string[] {
    return Array.from(this.pendingOperations.keys());
  }

  /**
   * 获取操作统计信息
   */
  getStats(): Record<string, any> {
    const pending = Array.from(this.pendingOperations.values());
    const now = Date.now();

    return {
      pendingCount: pending.length,
      totalExecutionTime: pending.reduce((sum, op) => sum + (now - op.startTime), 0),
      longestRunningOperation:
        pending.length > 0
          ? pending.reduce((longest, current) =>
              now - current.startTime > now - longest.startTime ? current : longest
            ).operationId
          : null,
      cacheSize: this.lastExecutionTimes.size,
    };
  }

  /**
   * 限制缓存大小
   */
  private limitCacheSize(): void {
    if (this.lastExecutionTimes.size > this.maxCacheSize) {
      // 删除最老的记录
      const entries = Array.from(this.lastExecutionTimes.entries());
      entries.sort((a, b) => a[1] - b[1]);

      const toDelete = entries.slice(0, entries.length - this.maxCacheSize);
      toDelete.forEach(([operationId]) => {
        this.lastExecutionTimes.delete(operationId);
      });

      logger.info(`[OperationGuard] 清理缓存，删除 ${toDelete.length} 条记录`);
    }
  }

  /**
   * 清理所有缓存
   */
  clearCache(): void {
    this.lastExecutionTimes.clear();
    logger.info('[OperationGuard] 缓存已清空');
  }

  /**
   * 销毁防护器
   */
  destroy(): void {
    this.cancelAll();
    this.clearCache();
    logger.info('[OperationGuard] 防护器已销毁');
  }

  /**
   * 获取调试信息
   */
  getDebugInfo(): Record<string, any> {
    return {
      pendingOperations: this.getPendingOperations(),
      stats: this.getStats(),
      cacheEntries: Array.from(this.lastExecutionTimes.entries()),
    };
  }
}
